<script setup lang="ts">
import { type AnimationPlaybackControlsWithThen, stagger, useAnimate, useInView } from 'motion-v'

const pinPositions = [[22, 14], [30, 40], [22, 58], [32, 85], [60, 10], [76, 14]]

const inputExpectedText = 'Best restaurants near me'

const [scope, animate] = useAnimate()
const isInView = useInView(scope, {
  amount: 'all',
})

const inputCursor = useTemplateRef('input-cursor')
const listItemOfRestaurant = useTemplateRef('restaurant-item')
const inputText = ref('')
const listItemOfRestaurantHeight = ref(0)

let beforeSequence: AnimationPlaybackControlsWithThen | undefined
let betweenSequence: AnimationPlaybackControlsWithThen | undefined
let afterSequence: AnimationPlaybackControlsWithThen | undefined

watch(isInView, async (inView) => {
  beforeSequence?.stop()
  betweenSequence?.stop()
  afterSequence?.stop()

  inputText.value = ''

  beforeSequence = animate([
    [
      '[data-id=pin]',
      { opacity: 0, y: 0 },
      { duration: 0 },
    ],
    [
      '[data-id=list]',
      { scale: 0.5, opacity: 0, y: -10 },
      { duration: 0 },
    ],
    [
      '[data-id=list] li:first-child',
      { display: 'none', height: 0, opacity: 0 },
      { duration: 0 },
    ],
    [
      '[data-id=list] li:last-child',
      { display: 'flex', height: listItemOfRestaurantHeight.value, opacity: 1 },
      { duration: 0 },
    ],
  ])

  await beforeSequence.finished

  if (!inView) return

  betweenSequence = animate(
    0, inputExpectedText.length,
    {
      duration: 1.2,
      ease: 'linear',
      onUpdate: (latest) => {
        inputText.value = inputExpectedText.slice(0, Math.ceil(latest))
      },
    },
  )

  await betweenSequence.finished

  afterSequence = animate([
    [
      '[data-id=pin]',
      { opacity: 1, y: 0 },
      { delay: stagger(0.08) },
    ],
    [
      '[data-id=list]',
      { opacity: 1, scale: 1, y: 0 },
      { at: '-0.5' },
    ],
    [
      '[data-id=list] li',
      { opacity: 1 },
      { delay: stagger(0.1) },
    ],
    [
      '[data-id=list] li:last-child',
      { opacity: 0, height: 0, display: 'none' },
      { delay: 1 },
    ],
    [
      '[data-id=list] li:first-child',
      { opacity: 1, height: listItemOfRestaurantHeight.value, display: 'flex' },
    ],
  ])

  await afterSequence.finished
})

onMounted(() => {
  listItemOfRestaurantHeight.value = listItemOfRestaurant.value!.clientHeight

  if (inputCursor.value) {
    animate(
      inputCursor.value,
      {
        opacity: [1, 1, 0, 0],
      },
      {
        duration: 1,
        repeat: Infinity,
        times: [0, 0.5, 0.5, 1],
      },
    )
  }
})
</script>

<template>
  <div
    ref="scope"
    class="relative place-self-center pb-32 xs:pb-10 xs:pt-0"
  >
    <div class="rounded-2xl bg-white p-4 shadow-lg ring-1 ring-neutral-400/10">
      <div class="relative">
        <img
          :src="`${S3_ASSETS_URL}/home/map.jpg`"
          class="size-full max-h-[20.475rem] max-w-[30.6875rem] rounded-xl object-cover opacity-70"
        >

        <img
          v-for="([y, x], pinIndex) in pinPositions"
          :key="pinIndex"
          :src="`${S3_ASSETS_URL}/pin.svg`"
          :style="{
            top: `${y}%`,
            left: `${x}%`,
            opacity: 0,
          }"
          data-id="pin"
          class="absolute h-10 w-8 object-contain"
          role="presentation"
        >
      </div>

      <div class="absolute inset-0 flex flex-col items-end justify-end gap-y-5">
        <div class="absolute inset-x-6 top-6 flex items-center gap-x-2 rounded-full bg-white px-3 py-2 font-medium shadow">
          <UiIcon
            name="magnifier-outline"
            class="text-2xl"
          />

          <div class="relative flex items-stretch gap-x-0.5">
            <span>{{ inputText }}</span>

            <span
              ref="input-cursor"
              class="flex w-px bg-neutral-400 opacity-0 will-change-[opacity]"
            />
          </div>
        </div>

        <ul
          data-id="list"
          class="mr-4 grid max-w-max gap-y-3 rounded-2xl bg-white p-4 shadow ring-1 ring-neutral-400/10 [counter-reset:rank] xs:mr-10"
          :style="{
            opacity: 0,
          }"
        >
          <li class="flex gap-x-7 overflow-clip rounded-xl bg-orange-500/5 shadow ring-2 ring-orange-200 lg:gap-x-14">
            <div class="flex items-center gap-x-2">
              <img
                :src="`${S3_ASSETS_URL}/home/restaurant.jpg`"
                class="block size-16 bg-neutral-200"
              >

              <div class="flex flex-col">
                <span class="text-base text-gray-800">
                  Your Restaurant
                </span>

                <div class="flex items-center text-lg text-orange-500">
                  <UiIcon
                    v-for="i in 4"
                    :key="i"
                    name="star-solid-rounded"
                  />

                  <UiIcon name="star-half-rounded" />

                  <span class="ml-1 font-bold leading-none">
                    4.7
                  </span>
                </div>
              </div>
            </div>

            <span class="ml-auto mr-1 mt-1 inline-flex size-6 shrink-0 items-center justify-center rounded-[50%] bg-orange-500 text-sm font-bold tabular-nums leading-none text-white before:content-[counter(rank)] before:[counter-increment:rank]" />
          </li>

          <li class="flex gap-x-7 overflow-clip rounded-xl bg-neutral-100 ring-1 ring-neutral-400/20 lg:gap-x-14">
            <div class="flex items-center gap-x-2">
              <span class="block size-14 bg-neutral-200" />

              <div class="flex flex-col">
                <span class="text-xs text-neutral-500">
                  Competitor A
                </span>

                <div class="flex items-center text-base text-neutral-500/80">
                  <UiIcon
                    v-for="i in 3"
                    :key="i"
                    name="star-solid-rounded"
                  />

                  <UiIcon name="star-half-rounded" />

                  <UiIcon name="star-outline-rounded" />

                  <span class="ml-1 font-bold leading-none">
                    3.8
                  </span>
                </div>
              </div>
            </div>

            <span class="ml-auto mr-1 mt-1 inline-flex size-5 shrink-0 items-center justify-center rounded-[50%] bg-neutral-200 text-xs font-bold tabular-nums leading-none text-neutral-400 before:content-[counter(rank)] before:[counter-increment:rank]" />
          </li>

          <li class="flex gap-x-7 overflow-clip rounded-xl bg-neutral-100 ring-1 ring-neutral-400/20 lg:gap-x-14">
            <div class="flex items-center gap-x-2">
              <span class="block size-14 bg-neutral-200" />

              <div class="flex flex-col">
                <span class="text-xs text-neutral-500">
                  Competitor B
                </span>

                <div class="flex items-center text-base text-neutral-500/80">
                  <UiIcon
                    v-for="i in 3"
                    :key="i"
                    name="star-solid-rounded"
                  />

                  <UiIcon name="star-half-rounded" />

                  <UiIcon name="star-outline-rounded" />

                  <span class="ml-1 font-bold leading-none">
                    3.6
                  </span>
                </div>
              </div>
            </div>

            <span class="ml-auto mr-1 mt-1 inline-flex size-5 shrink-0 items-center justify-center rounded-[50%] bg-neutral-200 text-xs font-bold tabular-nums leading-none text-neutral-400 before:content-[counter(rank)] before:[counter-increment:rank]" />
          </li>

          <li
            ref="restaurant-item"
            class="flex gap-x-7 overflow-clip rounded-xl bg-orange-500/5 shadow ring-2 ring-orange-200 lg:gap-x-14"
          >
            <div class="flex items-center gap-x-2">
              <img
                :src="`${S3_ASSETS_URL}/home/restaurant.jpg`"
                class="block size-16 bg-neutral-200"
              >

              <div class="flex flex-col">
                <span class="text-base text-gray-800">
                  Your Restaurant
                </span>

                <div class="flex items-center text-lg text-orange-500">
                  <UiIcon
                    v-for="i in 3"
                    :key="i"
                    name="star-solid-rounded"
                  />

                  <UiIcon name="star-half-rounded" />

                  <UiIcon name="star-outline-rounded" />

                  <span class="ml-1 font-bold leading-none">
                    3.4
                  </span>
                </div>
              </div>
            </div>

            <span class="ml-auto mr-1 mt-1 inline-flex size-6 shrink-0 items-center justify-center rounded-[50%] bg-orange-500 text-sm font-bold tabular-nums leading-none text-white before:content-[counter(rank)] before:[counter-increment:rank]" />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
